import { useQuery } from "@tanstack/react-query";

import { PastSessionImportModal, type ImportCourseOption, type ImportSubjectOption } from "../../../past_sessions";
import { listTherapistCouples } from "../../application/patients";
import { useGroupPatients } from "../../application/useGroupPatients";
import { usePatientOptions } from "../../application/usePatientOptions";
import type { CouplePatient } from "../../domain/CouplePatient";
import { groupLabel } from "../../domain/GroupPatient";
import { useSessionPreferences } from "../../../center";

interface Props {
    therapistId: string | undefined;
    onClose: () => void;
    /** A training centre's courses, for the module each session worked on. */
    courses?: ImportCourseOption[];
}

const coupleLabel = (couple: CouplePatient) =>
    couple.display_name ||
    [couple.patient_1?.first_name, couple.patient_2?.first_name].filter(Boolean).join(" & ");

/** The therapist's own history: their patients, couples and groups, read once the import is opened. */
export default function PastSessionsImport({ therapistId, onClose, courses }: Props) {
    const { options } = usePatientOptions(therapistId);
    const { data: couples = [] } = useQuery({
        queryKey: ["past-sessions-couples", therapistId],
        queryFn: () => listTherapistCouples(therapistId as string),
        enabled: Boolean(therapistId),
    });
    const { groups } = useGroupPatients();

    // Sessions only of the kinds the centre runs.
    const { modes } = useSessionPreferences();

    const subjects: ImportSubjectOption[] = [
        ...(modes.individual ? options : []).map((option) => ({ kind: "patient" as const, id: option.value, label: option.label })),
        ...(modes.couple ? couples : []).map((couple) => ({ kind: "couple" as const, id: couple.id, label: coupleLabel(couple) })),
        ...(modes.group ? groups : []).map((group) => ({ kind: "group" as const, id: group.id, label: groupLabel(group) })),
    ];

    return (
        <PastSessionImportModal
            isOpen
            onClose={onClose}
            target={{ kind: "therapist" }}
            subjects={subjects}
            courses={courses}
        />
    );
}
